import axios from 'axios';
import queryString from 'query-string';
import * as Sentry from 'utils/errorTracking';

// @ts-ignore
import ky from 'ky';
import { getAPI } from 'utils/helpers';
import { stripFroalaEditorChrome } from 'utils/sanitizeFroalaHtml';
import {
  applyPreviewStatusOverride,
  getPreviewOverrideFromLocation,
} from 'utils/offerings/previewStatusOverride';
import apiClient from 'lib/apiClient';

// Typescript
import { IndustryType } from 'typescriptUtils/types/offerings/search';

const SSR_TIMEOUT_MS = 5000;
const ssrKyOptions = { timeout: SSR_TIMEOUT_MS };
const ssrAxiosConfig = { timeout: SSR_TIMEOUT_MS };

const getErrorStatusCode = (error: unknown): number | undefined => {
  if (!error || typeof error !== 'object') {
    return undefined;
  }
  const err = error as { response?: { status?: unknown } };
  return typeof err.response?.status === 'number' ? err.response.status : undefined;
};

const isNotFoundError = (error: unknown): boolean => {
  return getErrorStatusCode(error) === 404;
};

const isServerError = (error: unknown): boolean => {
  const status = getErrorStatusCode(error);
  return status !== undefined && status >= 500 && status < 600;
};

const isUnauthorizedError = (error: unknown): boolean => {
  return getErrorStatusCode(error) === 401;
};

export const getOffering = async ({ slug }: { slug: string | string[] }) => {
  let parsedSlug = slug;

  if (Array.isArray(slug)) {
    parsedSlug = slug?.[0];
  }

  const isClient = typeof window !== 'undefined';
  const baseUrl = isClient
    ? `${getAPI()}/primary_facade/v2/offerings`
    : `${process.env.NEXT_PUBLIC_GATEWAY_URL}/primary_facade/v2/offerings`;

  try {
    if (isClient) {
      const { data } = await apiClient.get(`${baseUrl}/${parsedSlug}`, { timeout: SSR_TIMEOUT_MS });
      // PD-35758: apply the preview-only TTW→CF status override at the fetch
      // layer so every `['offeringData', slug]` consumer (hook + inline
      // useQuery) sees it. No-op outside `?preview=true&previewPageType=CF`
      // (legacy CF1/CF2 inputs are aliased to CF — PD-35901).
      return applyPreviewStatusOverride(data, getPreviewOverrideFromLocation());
    }
    const response = await ky(`${baseUrl}/${parsedSlug}`, ssrKyOptions).json();
    return response;
  } catch (error) {
    if (isNotFoundError(error)) {
      // 404s are expected for invalid/removed offerings - no Sentry reporting needed
    } else if (isServerError(error)) {
      // 5xx errors are upstream gateway issues; log as message with fingerprint to avoid flooding
      Sentry.captureMessage(`Gateway 5xx for offering: ${parsedSlug}`, {
        level: 'warning',
        fingerprint: ['gateway-5xx-offering', String(parsedSlug)],
        extra: { slug: parsedSlug, status: getErrorStatusCode(error) },
      });
    } else {
      Sentry.captureException(error);
    }
  }

  return null;
};

const BATCH_CONCURRENCY = 6;

export const getOfferingsBatch = async (slugs: string[]): Promise<Map<string, any>> => {
  const results = new Map<string, any>();
  const uniqueSlugs = Array.from(new Set(slugs));

  for (let i = 0; i < uniqueSlugs.length; i += BATCH_CONCURRENCY) {
    const chunk = uniqueSlugs.slice(i, i + BATCH_CONCURRENCY);
    const chunkResults = await Promise.allSettled(chunk.map((slug) => getOffering({ slug })));

    chunkResults.forEach((result, idx) => {
      if (result.status === 'fulfilled' && result.value) {
        results.set(chunk[idx], result.value);
      }
    });
  }

  return results;
};

export const getOfferingById = (offeringId: string) =>
  axios
    .get(`${getAPI()}/primary_facade/v2/offerings/${offeringId}`, ssrAxiosConfig)
    .then(({ data }) => data || {});

export const getAllOfferings = () =>
  axios
    .get(
      queryString.stringifyUrl(
        {
          url: `${getAPI()}/primary_facade/v2/offerings`,
          query: {
            status: 'approved,testing,closed',
            size: process.env.PR_CHECK ? 10 : 100000,
          },
        },
        {
          skipEmptyString: true,
        },
      ),
    )
    .then(({ data }) => data);

export const getArticles = ({ offeringId, pageParam }: { offeringId: string; pageParam: any }) =>
  axios
    .get(
      queryString.stringifyUrl(
        {
          url: `${getAPI()}/primary_facade/v2/offerings/${offeringId}/articles`,
          query: {
            page: pageParam,
          },
        },
        {
          skipEmptyString: true,
        },
      ),
    )
    .then(({ data }) => data);

export const getPitchDeck = async ({ primaryId }: { primaryId: string }) => {
  try {
    const isClient = typeof window !== 'undefined';
    let response;
    if (isClient) {
      const { data } = await apiClient.get(
        `${getAPI()}/primary_facade/offerings/${primaryId}/campaignPage`,
        { timeout: SSR_TIMEOUT_MS },
      );
      response = data;
    } else {
      response = await ky(
        `${getAPI()}/primary_facade/offerings/${primaryId}/campaignPage`,
        ssrKyOptions,
      ).json();
    }
    if (response && typeof (response as { html?: unknown }).html === 'string') {
      const r = response as { html: string };
      r.html = stripFroalaEditorChrome(r.html);
    }
    return response;
  } catch (error) {
    if (isNotFoundError(error)) {
      // Expected for offerings without a campaign page
    } else if (isServerError(error)) {
      Sentry.captureMessage(`Gateway 5xx for campaignPage: ${primaryId}`, {
        level: 'warning',
        fingerprint: ['gateway-5xx-campaignPage', String(primaryId)],
        extra: { primaryId, status: getErrorStatusCode(error) },
      });
    } else {
      Sentry.captureException(error);
    }
    throw error;
  }
};

export const getHighlights = async ({ primaryId }: { primaryId: string }) => {
  const isClient = typeof window !== 'undefined';
  const fetcher = isClient ? apiClient : axios;
  const { data } = await fetcher.get(
    `${getAPI()}/primary_facade/offerings/${primaryId}/highlights`,
    { timeout: SSR_TIMEOUT_MS },
  );
  return data;
};

export const getRewards = async ({ id }: { id: string }) => {
  const isClient = typeof window !== 'undefined';
  const fetcher = isClient ? apiClient : axios;
  const { data } = await fetcher.get(`${getAPI()}/primary_facade/v2/offerings/${id}/rewards`, {
    timeout: SSR_TIMEOUT_MS,
  });
  return data;
};

export const getAmountRaised = ({ offeringId }: { offeringId: string }) => {
  const isClient = typeof window !== 'undefined';
  const fetcher = isClient ? apiClient : axios;
  return fetcher
    .get(`${getAPI()}/primary_facade/v2/offerings/${offeringId}/amountRaised`, {
      timeout: SSR_TIMEOUT_MS,
    })
    .then(({ data }) => data);
};

export const isFollowing = (primaryUserId: any, idOrSlug: any, cookie: undefined) =>
  axios
    .head(
      queryString.stringifyUrl(
        {
          url: `${getAPI()}/primary_facade/v2/offerings?`,
          query: {
            primaryUserId,
            idOrSlug,
          },
        },
        {
          skipEmptyString: true,
        },
      ),
      {
        headers: cookie
          ? {
              cookie,
            }
          : {},
        withCredentials: !!cookie,
      },
    )
    .then((res) => res.status === 200);

export const startFollowing = (primaryUserId: any, idOrSlug: string) =>
  apiClient
    .post(
      queryString.stringifyUrl(
        {
          url: `${getAPI()}/primary_facade/v2/offerings/follow`,
          query: {
            primaryUserId,
            idOrSlug,
          },
        },
        {
          skipEmptyString: true,
        },
      ),
    )
    .then((res) => ({ success: res.status === 200 || res.status === 201, status: res.status }));

export const unFollow = (primaryUserId: any, idOrSlug: string) =>
  apiClient
    .post(
      queryString.stringifyUrl(
        {
          url: `${getAPI()}/primary_facade/v2/offerings/unfollow`,
          query: {
            primaryUserId,
            idOrSlug,
          },
        },
        {
          skipEmptyString: true,
        },
      ),
    )
    .then((res) => res.status === 200);

export const getComments = ({
  primaryId,
  pageParam = 0,
  parentId,
  size = 10,
}: {
  primaryId: string;
  pageParam?: number;
  parentId?: string;
  size?: number;
}) =>
  axios
    .get(
      queryString.stringifyUrl(
        {
          url: `${getAPI()}/comment/comment/search`,
          query: {
            externalEntityId: primaryId,
            page: pageParam,
            size,
            parentId,
          },
        },
        {
          skipEmptyString: true,
        },
      ),
    )
    .then(({ data }) => data);

export const getUserComments = ({
  primaryUserId,
  cookie,
}: {
  primaryUserId: string;
  cookie: string | undefined;
}) =>
  axios
    .get(
      queryString.stringifyUrl(
        {
          url: `${getAPI()}/comment/user`,
          query: {
            primaryUserId,
          },
        },
        {
          skipEmptyString: true,
        },
      ),
      {
        headers: cookie
          ? {
              cookie,
            }
          : {},
        withCredentials: !!cookie,
      },
    )
    .then(({ data }) => data);

export const commentAction = ({ id, action }: { id: string; action: string }) =>
  apiClient
    .post(
      queryString.stringifyUrl(
        {
          url: `${getAPI()}/comment/comment/${id}/actions/${action}`,
        },
        {
          skipEmptyString: true,
        },
      ),
    )
    .then(({ data }) => data);

export const createComment = ({
  externalId,
  body,
  parentId,
  user,
}: {
  externalId: string;
  body: string;
  parentId?: string;
  user: string;
}) =>
  apiClient
    .post(
      queryString.stringifyUrl(
        {
          url: `${getAPI()}/comment/comment`,
        },
        {
          skipEmptyString: true,
        },
      ),
      {
        externalId,
        body,
        parentId,
        user,
      },
    )
    .then(({ data }) => data);

export const getAudienceRewards = (offeringId: any, userId: any, cookie: undefined) =>
  axios
    .get(
      queryString.stringifyUrl(
        {
          url: `${getAPI()}/primary_facade/v2/users/${userId}/loyaltyBonus?offeringId=${offeringId}`,
        },
        {
          skipEmptyString: true,
        },
      ),
      {
        headers: cookie
          ? {
              cookie,
            }
          : {},
        withCredentials: !!cookie,
      },
    )
    .then(({ data }) => data)
    .catch((error) => {
      if (isNotFoundError(error)) {
        return null;
      }

      Sentry.captureException(error);
      throw error;
    });

export const getUpdates = ({
  offeringId,
  pageParam,
  size,
  recentlyApproved,
  updateSelector,
}: {
  offeringId: string;
  pageParam: any;
  size: number;
  recentlyApproved: boolean;
  updateSelector: string;
}) =>
  axios
    .get(
      queryString.stringifyUrl(
        {
          url: `${getAPI()}/primary_facade/v2/offerings/${offeringId}/updates`,
          query: {
            statuses: 'APPROVED',
            page: pageParam,
            size,
            recentlyApproved,
            updateSelector,
          },
        },
        {
          skipEmptyString: true,
        },
      ),
    )
    .then(({ data }) => data);

export const getUpdatesCampaignService = ({
  offeringId,
  pageParam,
  size,
  recentlyApproved,
  updateSelector,
  statuses,
}: {
  offeringId: string;
  pageParam: any;
  size: number;
  recentlyApproved: boolean;
  updateSelector: string;
  statuses: string;
}) =>
  axios
    .get(
      queryString.stringifyUrl(
        {
          url: `${getAPI()}/campaign/offeringUpdate/offering/${offeringId}`,
          query: {
            page: pageParam,
            size,
            recentlyApproved,
            updateSelector,
            statuses,
          },
        },
        {
          skipEmptyString: true,
        },
      ),
    )
    .then(({ data }) => data);

export const getEntityOwnerForOffering = ({
  externalId,
  cookie,
}: {
  externalId: string;
  cookie: string | undefined;
}) =>
  axios
    .get(
      queryString.stringifyUrl(
        {
          url: `${getAPI()}/comment/owningEntity/owningUser`,
          query: {
            externalId,
          },
        },
        {
          skipEmptyString: true,
        },
      ),
      {
        headers: cookie
          ? {
              cookie,
            }
          : {},
        withCredentials: !!cookie,
      },
    )
    .then(({ data }) => data);

export const getCurrentDraftVersionRequest = async ({ campaignId }: { campaignId: any }) => {
  try {
    const response = await ky(
      queryString.stringifyUrl(
        {
          url: `${getAPI()}/campaign/campaignPage/${campaignId}`,
          query: {
            versionStatus: 'DRAFT',
          },
        },
        {
          skipEmptyString: true,
        },
      ),
      ssrKyOptions,
    ).json();

    return response;
  } catch (error) {
    if (!isNotFoundError(error)) {
      Sentry.captureException(error);
    }
    throw error;
  }
};

export const getUserRequest = async () => {
  try {
    const response = await ky(`${getAPI()}/user_svc/user/validateJwt`, ssrKyOptions).json();
    return response;
  } catch (error) {
    Sentry.captureException(error);
    throw error;
  }
};

export const getOfferingUpdates = async ({
  offeringId,
  pageParam,
  size,
  updateSelector,
  recentlyApproved,
}: {
  offeringId: string;
  pageParam: any;
  size: number;
  updateSelector: string;
  recentlyApproved: boolean;
}) => {
  try {
    const response = await ky(
      queryString.stringifyUrl(
        {
          url: `${getAPI()}/campaign/offeringUpdate/offering/${offeringId}/public`,
          query: {
            page: pageParam,
            size,
            updateSelector,
            recentlyApproved,
          },
        },
        {
          skipEmptyString: true,
        },
      ),
      ssrKyOptions,
    ).json();

    return response;
  } catch (error) {
    Sentry.captureException(error);
    return {
      list: [],
      pageNumber: 0,
      totalPages: 0,
    };
  }
};

export const getPublishedOfferingUpdates = ({
  size = 25,
  page = 0,
}: {
  size: number;
  page: number;
}) =>
  axios
    .get(
      queryString.stringifyUrl({
        url: `${getAPI()}/campaign/offeringUpdate`,
        query: {
          status: 'PUBLISHED',
          page,
          size,
        },
      }),
    )
    .then(({ data }) => data);

export const getOfferingUpdateById = (id: string | null, privateUpdateToken?: string | null) => {
  if (!id) return Promise.resolve(null);

  // Construct the URL, including the private_update_token when it's provided
  const url = privateUpdateToken
    ? `${getAPI()}/campaign/offeringUpdate/${id}?private_update_token=${privateUpdateToken}`
    : `${getAPI()}/campaign/offeringUpdate/${id}`;

  // For non-private requests, set empty Cookie header
  const config = !privateUpdateToken
    ? {
        headers: {
          Cookie: '',
        },
      }
    : undefined;

  return axios.get(url, { ...config, timeout: SSR_TIMEOUT_MS }).then(({ data }) => data);
};

export const getOfferingIndustriesRequest = (): Promise<IndustryType[]> => {
  return axios.get(`${getAPI()}/primary_facade/v2/offerings/industries?midLevelOnly=true`);
};

export const getOfferingVerticalsRequest = () => {
  return axios.get(`${getAPI()}/primary_facade/v2/offerings/verticals`);
};

export const getOfferingHero = async ({ slug }: { slug: string | string[] }) => {
  let parsedSlug = slug;

  if (Array.isArray(slug)) {
    parsedSlug = slug?.[0];
  }

  // Return null immediately if no slug is provided
  if (!parsedSlug) {
    return null;
  }

  try {
    const response = await ky(
      `${getAPI()}/primary_facade/v2/offerings/${parsedSlug}/hero`,
      ssrKyOptions,
    ).json();

    // Ensure we always return a valid object or null, never undefined
    return response || null;
  } catch (error) {
    if (isNotFoundError(error)) {
      // Expected for offerings without hero data
    } else if (isServerError(error)) {
      Sentry.captureMessage(`Gateway 5xx for offering hero: ${parsedSlug}`, {
        level: 'warning',
        fingerprint: ['gateway-5xx-offering-hero', String(parsedSlug)],
        extra: { slug: parsedSlug, status: getErrorStatusCode(error) },
      });
    } else {
      Sentry.captureException(error);
    }
    // Explicitly return null to prevent undefined from being serialized during hydration
    return null;
  }
};

/**
 * Get user information from cookies for server-side requests.
 *
 * Returns the full user payload from `validateJwt` (passed through as-is),
 * which includes at least `primaryUserId`, `roles`, and `email`. Returns
 * `{primaryUserId: null, roles: []}` when no auth cookies are present.
 *
 * @param cookie - The cookie string from the request
 */
export const getUserFromCookie = async (
  cookie?: string,
): Promise<{
  primaryUserId?: string | null;
  roles?: string[];
  email?: string;
  [key: string]: unknown;
}> => {
  if (!cookie) {
    return { primaryUserId: null, roles: [] };
  }

  const hasAuthCookies = cookie.includes('_se_access_3=') || cookie.includes('_se_refresh_3=');
  if (!hasAuthCookies) {
    return { primaryUserId: null, roles: [] };
  }

  try {
    const response = await axios.get(`${getAPI()}/user_svc/user/validateJwt`, {
      headers: {
        cookie,
      },
      withCredentials: true,
      timeout: SSR_TIMEOUT_MS,
    });
    return response.data || { primaryUserId: null, roles: [] };
  } catch (error) {
    if (isUnauthorizedError(error)) {
      Sentry.captureMessage(
        'SSR validateJwt returned 401 — user has auth cookies but tokens are expired',
        {
          level: 'warning',
          fingerprint: ['ssr-validatejwt-401'],
        },
      );
    } else {
      Sentry.captureException(error);
    }
    return { primaryUserId: null, roles: [] };
  }
};

/**
 * Check if a user has restricted access to an offering
 * @param offeringId - The ID of the offering to check access for
 * @param primaryUserId - The primary user ID
 * @param cookie - Optional cookie for server-side requests
 * @returns Promise<{hasAccess: boolean}> - Whether the user has restricted access
 */
export const checkRestrictedAccess = async ({
  offeringId,
  primaryUserId,
  cookie,
}: {
  offeringId: string;
  primaryUserId: string;
  cookie?: string;
}) => {
  const apiUrl = `${getAPI()}/primary_facade/v2/offerings/${offeringId}/restrictedAccess/user/${primaryUserId}`;

  try {
    const response = await axios.get(apiUrl, {
      headers: cookie
        ? {
            cookie,
          }
        : {},
      withCredentials: !!cookie,
      timeout: SSR_TIMEOUT_MS,
    });
    if (typeof response.data === 'boolean') {
      return { hasAccess: response.data };
    }
    if (response.data && typeof response.data.hasAccess === 'boolean') {
      return response.data;
    }
    return { hasAccess: false };
  } catch {
    return { hasAccess: true };
  }
};

export const getLiveStreamForOffering = async ({ slug }: { slug: string }): Promise<any | null> => {
  try {
    const response = await ky(
      `${process.env.NEXT_PUBLIC_GATEWAY_URL}/get_live_stream_by_slug/${slug}`,
      ssrKyOptions,
    ).json();

    return response;
  } catch (error) {
    if (!isNotFoundError(error)) {
      Sentry.captureException(error);
    }
    return null;
  }
};

/**
 * Submit Canadian investor to Waverly broker dealer
 * @param offeringId - The offering UUID
 * @param primaryUserId - The user's primary user ID
 * @param address - Full address object with addressLine1, addressLine2, city, province, country, postalCode
 * @param accreditedStatus - 'accredited' or 'eligible'
 */
export const submitCanadianInvestor = async ({
  offeringId,
  primaryUserId,
  addressLine1,
  addressLine2,
  city,
  province,
  country,
  postalCode,
  accreditedStatus,
}: {
  offeringId: string;
  primaryUserId: string;
  addressLine1: string;
  addressLine2?: string;
  city: string;
  province: string;
  country: string;
  postalCode: string;
  accreditedStatus: string;
}): Promise<{ success: boolean; error?: string }> => {
  try {
    const response = await apiClient.post(
      `${getAPI()}/primary_facade/brokerDealer/external/waverly/newInvestor`,
      {
        offeringId,
        primaryUserId,
        addressLine1,
        addressLine2: addressLine2 || '',
        city,
        province,
        country,
        postalCode,
        accreditedStatus,
      },
      {
        withCredentials: true,
      },
    );

    return { success: true, ...response.data };
  } catch (error: any) {
    Sentry.captureException(error);

    let errorMessage = 'Failed to submit investor information';
    if (error?.response?.data?.message) {
      errorMessage = error.response.data.message;
    }

    return { success: false, error: errorMessage };
  }
};

export interface CaplightOfferingData {
  offeringId: string;
  slug: string;
  name: string;
  lastRoundPrice: string;
  lastRoundValuation: string;
  lastRoundDate: string;
  lastRoundPriceRaw: number;
  lastRoundValuationRaw: number;
}

export const getCaplightPricing = async (
  offeringIds: string[],
): Promise<{ offerings: CaplightOfferingData[] }> => {
  if (!offeringIds.length) return { offerings: [] };
  try {
    const { data } = await axios.post(
      `${getAPI()}/primary_facade/v2/offerings/caplight`,
      offeringIds,
    );
    return data;
  } catch (error) {
    Sentry.captureException(error);
    return { offerings: [] };
  }
};
